import React from 'react';
import { LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Nurse, NURSE_TYPE_THEME } from '../../types/nurse';
import NurseTableRow from './NurseTableRow';
import { CommonTablePagination } from '../common';

interface NurseTableProps {
  nurses: Nurse[];
  trainingMode: boolean;
  loading: boolean;
  totalCount: number;
  page: number;
  rowsPerPage: number;
  onChangePage: (event: unknown, newPage: number) => void;
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  searchParams: any;
}

/**
 * 看護師一覧テーブル
 */
const NurseTable: React.FC<NurseTableProps> = ({
  nurses,
  trainingMode,
  loading,
  totalCount,
  page,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  searchParams
}) => {
  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {loading && <LinearProgress/>}
      <TableContainer>
        <Table stickyHeader aria-label="看護師一覧">
          <TableHead>
            <TableRow>
              {/* 対話状態/研修ステータス列 */}
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: trainingMode ? NURSE_TYPE_THEME.TRAINING.BACKGROUND : NURSE_TYPE_THEME.NORMAL.BACKGROUND,
                  borderBottom: trainingMode ? `2px solid ${NURSE_TYPE_THEME.TRAINING.BORDER}` : `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`,
                  minWidth: trainingMode ? '110px' : 'auto' // 研修ステータス列の幅を広げる
                }}
              >
                {trainingMode ? 'ステータス' : '対話状態'}
              </TableCell>

              {/* 研修修了回数 - 研修看護師モードでのみ表示 */}
              {trainingMode && (
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: NURSE_TYPE_THEME.TRAINING.BACKGROUND,
                    borderBottom: `2px solid ${NURSE_TYPE_THEME.TRAINING.BORDER}`
                  }}
                >
                  修了回数
                </TableCell>
              )}

              {/* 最新の研修チャット - 研修看護師モードでのみ表示 */}
              {trainingMode && (
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: NURSE_TYPE_THEME.TRAINING.BACKGROUND,
                    borderBottom: `2px solid ${NURSE_TYPE_THEME.TRAINING.BORDER}`,
                    minWidth: '180px', // 「最終: xxx 日前」が改行されない程度の最小幅
                    width: '180px'
                  }}
                >
                  最新の研修
                </TableCell>
              )}

              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: trainingMode ? NURSE_TYPE_THEME.TRAINING.BACKGROUND : NURSE_TYPE_THEME.NORMAL.BACKGROUND,
                  borderBottom: trainingMode ? `2px solid ${NURSE_TYPE_THEME.TRAINING.BORDER}` : `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`
                }}
              >
                ロール
              </TableCell>

              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: trainingMode ? NURSE_TYPE_THEME.TRAINING.BACKGROUND : NURSE_TYPE_THEME.NORMAL.BACKGROUND,
                  borderBottom: trainingMode ? `2px solid ${NURSE_TYPE_THEME.TRAINING.BORDER}` : `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`
                }}
              >
                名前 / ニックネーム
              </TableCell>

              <TableCell
                align="left"
                sx={{
                  fontWeight: 'bold',
                  minWidth: '130px',
                  backgroundColor: trainingMode ? NURSE_TYPE_THEME.TRAINING.BACKGROUND : NURSE_TYPE_THEME.NORMAL.BACKGROUND,
                  borderBottom: trainingMode ? `2px solid ${NURSE_TYPE_THEME.TRAINING.BORDER}` : `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`,
                  textAlign: 'left'
                }}
              >
                性別 (年齢)
              </TableCell>

              {/* 研修看護師モードでは受付状態を非表示 */}
              {!trainingMode && (
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: NURSE_TYPE_THEME.NORMAL.BACKGROUND,
                    borderBottom: `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`
                  }}
                >
                  受付状態
                </TableCell>
              )}

              <TableCell
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: trainingMode ? NURSE_TYPE_THEME.TRAINING.BACKGROUND : NURSE_TYPE_THEME.NORMAL.BACKGROUND,
                  borderBottom: trainingMode ? `2px solid ${NURSE_TYPE_THEME.TRAINING.BORDER}` : `2px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`
                }}
              >
                メールアドレス
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {nurses.map((nurse) => (
              <NurseTableRow
                key={nurse.id}
                nurse={nurse}
                trainingMode={trainingMode}
              />
            ))}
            {nurses.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={trainingMode ? 7 : 6} align="center">
                  {searchParams.talkStatus ? (
                    searchParams.talkStatus === 'IN_TALK' ?
                      '対話中の看護師がいません' :
                      '待機中の看護師がいません'
                  ) : '看護師データがありません'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CommonTablePagination
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
    </Paper>
  );
};
export default NurseTable;
